import { useMutation, useQueryClient } from '@tanstack/react-query';
import { themeImportErrorMessage } from '@/api/theme-import-history.contract';
import { themeImportHistoryQueryKey } from '@/api/theme-import-history.api';
import { addThemeQuestion, deactivateThemeQuestions, deleteThemeQuestions, importThemeQuestions, reactivateThemeQuestions } from '@/api/theme-question-curation.api';
import type { ThemeImportStatusFilter } from '@/api/theme-import-history.api';
import type { AddThemeQuestionInput } from '@/api/theme-question-curation.contract';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { useThemeImportQuestions } from './use-theme-import-questions';

export function useThemeQuestionCuration(themeId: string, isOpen: boolean, status: ThemeImportStatusFilter) {
  const { t } = useApp();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const copy = t.admin.excelFiles.themes.questions;
  const query = useThemeImportQuestions(themeId, isOpen, status);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['theme-import-questions', themeId] });
    void queryClient.invalidateQueries({ queryKey: themeImportHistoryQueryKey });
  };

  const addMutation = useMutation({
    mutationFn: (input: AddThemeQuestionInput) => addThemeQuestion(themeId, input),
    onSuccess: () => {
      toast({ title: t.success, description: copy.addSuccess });
      invalidate();
    },
    onError: (error) => toast({
      title: t.error,
      description: themeImportErrorMessage(error, copy.errors),
      variant: 'destructive',
    }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (ids: string[]) => deactivateThemeQuestions(themeId, ids),
    onSuccess: (result) => {
      toast({
        title: t.success,
        description: copy.deactivateSuccess.replace('{count}', String(result.data.deactivated)),
      });
      invalidate();
    },
    onError: (error) => toast({
      title: t.error,
      description: themeImportErrorMessage(error, copy.errors),
      variant: 'destructive',
    }),
  });

  const reactivateMutation = useMutation({
    mutationFn: (ids: string[]) => reactivateThemeQuestions(themeId, ids),
    onSuccess: (result) => {
      toast({ title: t.success, description: copy.reactivateSuccess.replace('{count}', String(result.data.reactivated)) });
      invalidate();
    },
    onError: (error) => toast({
      title: t.error,
      description: themeImportErrorMessage(error, copy.errors),
      variant: 'destructive',
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ ids, password }: { ids: string[]; password: string }) => deleteThemeQuestions(themeId, ids, password),
    onSuccess: (result) => {
      toast({ title: t.success, description: copy.deleteSuccess.replace('{count}', String(result.data.deleted)) });
      invalidate();
    },
    onError: (error) => toast({
      title: t.error,
      description: themeImportErrorMessage(error, copy.errors),
      variant: 'destructive',
    }),
  });

  const importMutation = useMutation({
    mutationFn: ({ file, language }: { file: File; language: string }) => importThemeQuestions(themeId, file, language),
    onSuccess: (result) => {
      toast({ title: t.success, description: copy.importSuccess
        .replace('{added}', String(result.data.added))
        .replace('{skipped}', String(result.data.skipped)) });
      invalidate();
    },
    onError: (error) => toast({
      title: t.error,
      description: themeImportErrorMessage(error, copy.errors),
      variant: 'destructive',
    }),
  });

  return { query, addMutation, deactivateMutation, reactivateMutation, deleteMutation, importMutation };
}
